import React, { useState, useEffect, useRef } from "react";
import { useQuizContext } from "../context/QuizContext";
import LoadingScreen from "../utils/LoadingScreen";

const Registration = () => {
  const { 
    userData, 
    updateUserData, 
    startQuiz, 
    setCurrentStep,
    isSubmitting,
    serverError,
    clearServerError
  } = useQuizContext();
  
  const [errors, setErrors] = useState({});
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showAlreadyRegisteredModal, setShowAlreadyRegisteredModal] = useState(false);

  // Estado para el autocompletado de email
  const [showEmailSuggestions, setShowEmailSuggestions] = useState(false);
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const [typingEmail, setTypingEmail] = useState(userData.email || "");
  const emailInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Colores que coinciden con Welcome
  const panelBgColor = "#030645"; // Azul marino oscuro
  const buttonColor = "#c84357"; // Rojo similar al de los elementos del logo

  // URL de la imagen para el LoadingScreen
  const loadingImageUrl = "https://imagenes-commerk.s3.us-east-2.amazonaws.com/Slider-IA-Licor-Ideal_Mesa-de-trabajo-1_1_.webp";

  // URL para política de tratamiento de datos
  const privacyPolicyUrl =
    "https://tiendacommerkvalle.com.co/wp-content/uploads/2025/02/politicas-tratamiento-datos.pdf";

  // Mostrar modal si hay un error de usuario ya registrado
  useEffect(() => {
    if (serverError && serverError.type === 'alreadyRegistered') {
      setShowAlreadyRegisteredModal(true);
    }
  }, [serverError]);

  // Dominios comunes para autocompletado
  const commonDomains = [
    "gmail.com",
    "hotmail.com",
    "outlook.com",
    "yahoo.com",
    "icloud.com",
    "live.com",
    "msn.com",
    "protonmail.com",
    "outlook.es",
    "yahoo.es",
  ];

  // Opciones de rango de edad
  const ageRanges = [
    { value: "18-25", label: "18 – 25 años" },
    { value: "26-35", label: "26 – 35 años" },
    { value: "36-55", label: "36 – 55 años" },
    { value: "56+", label: "56+ años" },
  ];

  // Función para validar email
  const validateEmail = (email) => {
    // Expresión regular más completa para validar emails
    const emailRegex =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailRegex.test(email);
  };

  // Función para generar sugerencias de email
  useEffect(() => {
    if (typingEmail.includes("@") && typingEmail.split("@")[1] !== "") {
      const prefix = typingEmail.split("@")[0];
      const partialDomain = typingEmail.split("@")[1].toLowerCase();

      const filteredDomains = commonDomains.filter((domain) =>
        domain.startsWith(partialDomain)
      );

      if (filteredDomains.length > 0) {
        const suggestions = filteredDomains.map(
          (domain) => `${prefix}@${domain}`
        );
        setEmailSuggestions(suggestions);
        setShowEmailSuggestions(true);
      } else {
        setShowEmailSuggestions(false);
      }
    } else {
      setShowEmailSuggestions(false);
    }
  }, [typingEmail]);

  // Efecto para cerrar las sugerencias al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        emailInputRef.current &&
        !emailInputRef.current.contains(event.target)
      ) {
        setShowEmailSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Función para validar los datos del formulario
  const validateForm = () => {
    const newErrors = {};

    // Validar nombre
    if (!userData.name.trim()) {
      newErrors.name = "El nombre es obligatorio";
    }

    // Validar email
    if (!userData.email.trim()) {
      newErrors.email = "El email es obligatorio";
    } else if (!validateEmail(userData.email)) {
      newErrors.email = "El formato del email no es válido";
    }

    // Validar teléfono
    if (!userData.phone.trim()) {
      newErrors.phone = "El teléfono es obligatorio";
    } else if (!/^\d{10}$/.test(userData.phone.replace(/\D/g, ""))) {
      newErrors.phone = "El formato del teléfono no es válido (10 dígitos)";
    }

    // Validar rango de edad
    if (!userData.ageRange) {
      newErrors.ageRange = "Por favor selecciona tu rango de edad";
    }

    // Validar consentimiento
    if (!userData.dataConsent) {
      newErrors.dataConsent = "Debes aceptar el tratamiento de datos";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Función para manejar los cambios en los inputs
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "email") {
      setTypingEmail(value);
    }

    updateUserData({
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Función para seleccionar una sugerencia de email
  const selectEmailSuggestion = (suggestion) => {
    updateUserData({ email: suggestion });
    setTypingEmail(suggestion);
    setShowEmailSuggestions(false);
    emailInputRef.current.focus();
  };

  // Función para manejar el envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validamos el formulario
    if (!validateForm()) {
      return;
    }

    try {
      // Iniciamos el quiz con las preguntas obtenidas
      await startQuiz();
    } catch (error) {
      console.error("Error al registrar:", error);
      // No mostramos alerta aquí ya que el modal se encargará de mostrar el error
    }
  };

  // Función para cerrar el modal y volver a la página de bienvenida
  const handleReturnToWelcome = () => {
    setShowAlreadyRegisteredModal(false);
    clearServerError();
    setCurrentStep(0); // Volver a la pantalla de Welcome
  };

  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen p-4"
      style={{
        backgroundImage: "url(/assets/images/background.jpg)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div
        className="max-w-md w-full rounded-xl shadow-lg bg-opacity-90"
        style={{ backgroundColor: panelBgColor }}
      >
        {/* Logo dentro del panel azul con tamaño adecuado */}
        <div className="flex justify-center mt-4">
          <img
            src="/assets/logos/logo3.jpeg"
            alt="COMMERK s.a.s."
            className="h-48"
          />
        </div>

        <div className="px-8 pb-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setCurrentStep(0)}
              className="text-white hover:text-opacity-80 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <h2 className="text-2xl font-bold text-center text-white">
              Registro
            </h2>
            <div className="w-6"></div>{" "}
            {/* Espaciador para centrar el título */}
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Campo de nombre */}
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium mb-1 text-white"
              >
                Nombre completo *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={userData.name}
                onChange={handleInputChange}
                className={`w-full px-4 py-2 bg-white bg-opacity-20 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-white ${
                  errors.name ? "border-2 border-red-500" : ""
                }`}
                placeholder="Ingresa tu nombre completo"
              />
              {errors.name && (
                <p className="mt-1 text-xs text-red-400">{errors.name}</p>
              )}
            </div>

            {/* Campo de email con autocompletado */}
            <div className="relative">
              <label
                htmlFor="email"
                className="block text-sm font-medium mb-1 text-white"
              >
                Correo electrónico *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                ref={emailInputRef}
                value={userData.email}
                onChange={handleInputChange}
                className={`w-full px-4 py-2 bg-white bg-opacity-20 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-white ${
                  errors.email ? "border-2 border-red-500" : ""
                }`}
                placeholder="ejemplo@correo.com"
                autoComplete="off"
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-400">{errors.email}</p>
              )}

              {/* Lista de sugerencias para autocompletado de email */}
              {showEmailSuggestions && emailSuggestions.length > 0 && (
                <div
                  ref={suggestionsRef}
                  className="absolute z-10 w-full mt-1 bg-gray-800 border border-gray-700 rounded-md shadow-lg max-h-60 overflow-auto"
                >
                  <ul>
                    {emailSuggestions.map((suggestion, index) => (
                      <li
                        key={index}
                        className="px-4 py-2 cursor-pointer hover:bg-gray-700 text-white text-sm"
                        onClick={() => selectEmailSuggestion(suggestion)}
                      >
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Campo de teléfono */}
            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium mb-1 text-white"
              >
                Número de teléfono *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={userData.phone}
                onChange={handleInputChange}
                className={`w-full px-4 py-2 bg-white bg-opacity-20 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-white ${
                  errors.phone ? "border-2 border-red-500" : ""
                }`}
                placeholder="(123) 456-7890"
              />
              {errors.phone && (
                <p className="mt-1 text-xs text-red-400">{errors.phone}</p>
              )}
            </div>

            {/* Selector de rango de edad */}
            <div>
              <label
                htmlFor="ageRange"
                className="block text-sm font-medium mb-1 text-white"
              >
                Rango de edad *
              </label>
              <div className="relative">
                <select
                  id="ageRange"
                  name="ageRange"
                  value={userData.ageRange || ""}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 bg-white bg-opacity-20 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-white appearance-none ${
                    errors.ageRange ? "border-2 border-red-500" : ""
                  }`}
                >
                  <option value="" disabled>
                    Selecciona tu rango de edad
                  </option>
                  {ageRanges.map((range) => (
                    <option
                      key={range.value}
                      value={range.value}
                      className="bg-gray-800 text-white"
                    >
                      {range.label}
                    </option>
                  ))}
                </select>
                {/* Ícono de flecha para el selector */}
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-white">
                  <svg
                    className="fill-current h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                  >
                    <path
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                      fillRule="evenodd"
                    ></path>
                  </svg>
                </div>
              </div>
              {errors.ageRange && (
                <p className="mt-1 text-xs text-red-400">{errors.ageRange}</p>
              )}
            </div>

            {/* Checkbox de consentimiento con botón de política de datos */}
            <div className="flex flex-col mt-3">
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    id="dataConsent"
                    name="dataConsent"
                    checked={userData.dataConsent}
                    onChange={handleInputChange}
                    className="w-4 h-4 bg-white bg-opacity-20 rounded focus:ring-red-500 focus:ring-2"
                  />
                </div>
                <div className="ml-3">
                  <label htmlFor="dataConsent" className="text-sm text-white">
                    Acepto el tratamiento de mis datos personales de acuerdo con
                    la política de tratamiento de datos *
                  </label>
                  {errors.dataConsent && (
                    <p className="mt-1 text-xs text-red-400">
                      {errors.dataConsent}
                    </p>
                  )}
                </div>
              </div>

              {/* Botón para ver la política de tratamiento de datos */}
              <div className="flex justify-center mt-3">
                <button
                  type="button"
                  onClick={() => setShowPrivacyModal(true)}
                  className="text-white underline text-sm hover:text-gray-300"
                  style={{ color: "#eae7e7" }}
                >
                  Política de Tratamiento de Datos
                </button>
              </div>
            </div>

            {/* Botón de envío */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-full shadow-md transition-all duration-300 hover:shadow-lg text-white font-bold py-2 px-4 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: buttonColor }}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    <span>Cargando...</span>
                  </div>
                ) : (
                  "Comenzar experiencia"
                )}
              </button>
            </div>

            <p className="text-xs text-center mt-2 text-white opacity-80">
              * Campos obligatorios
            </p>
          </form>
        </div>
      </div>

      {/* Nuevo LoadingScreen con imagen personalizada */}
      {isSubmitting && (
        <LoadingScreen 
        message="¡Genial! Nuestra IA🤖 esta preparando todo para ti. En breve podrás comenzar tu experiencia."
        imageUrl={loadingImageUrl}
        />
      )}

      {/* Modal de Política de Tratamiento de Datos */}
      {showPrivacyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-screen overflow-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">
                  Política de Tratamiento de Datos
                </h2>
                <button
                  onClick={() => setShowPrivacyModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    ></path>
                  </svg>
                </button>
              </div>
              <div className="text-center mb-6">
                <p className="mb-4">
                  Para consultar nuestra política de tratamiento de datos
                  personales, haga clic en el siguiente botón:
                </p>
                <a
                  href={privacyPolicyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Ver Política de Tratamiento de Datos
                </a>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowPrivacyModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Usuario Ya Registrado */}
      {showAlreadyRegisteredModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full overflow-hidden shadow-xl">
            <div className="p-6">
              <div className="flex flex-col items-center mb-6">
                {/* Ícono de información */}
                <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mb-4">
                  <svg 
                    className="w-10 h-10 text-yellow-600" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth="2" 
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                </div>
                
                <h2 className="text-2xl font-bold text-center text-gray-800">
                  ¡Ya has participado!
                </h2>
                
                <p className="text-center text-gray-600 mt-4">
                  Hemos detectado que ya has realizado esta experiencia previamente con el número de teléfono proporcionado.
                </p>
                
                <p className="text-center text-gray-600 mt-2">
                  Gracias por tu interés en nuestros productos. ¡Esperamos verte pronto en nuestras próximas experiencias!
                </p>
              </div>
              
              <div className="flex justify-center">
                <button
                  onClick={handleReturnToWelcome}
                  className="px-6 py-2 rounded-full text-white font-bold shadow-md transition-all duration-300 hover:shadow-lg"
                  style={{ backgroundColor: buttonColor }}
                >
                  Volver al inicio
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Registration;